import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Save, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, CATEGORIES, CITIES } from "./constants";

export function ProductForm({ product, onSave, onCancel, password }) {
  const [name, setName] = useState(product?.name || "");
  const [price, setPrice] = useState(product?.price || "");
  const [category, setCategory] = useState(product?.category || "standard");
  const [city, setCity] = useState(product?.city || "antalya");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(product?.image || null);
  const [saving, setSaving] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !price) return toast.error("Заполните название и цену");
    if (!product && !imageFile) return toast.error("Загрузите фото");

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("password", password);
      formData.append("name", name);
      formData.append("price", parseFloat(price));
      formData.append("category", category);
      formData.append("city", city);
      formData.append("description", "");
      if (imageFile) formData.append("image", imageFile);

      if (product) {
        await axios.put(`${API}/admin/products/${product.id}`, formData);
        toast.success("Букет обновлён");
      } else {
        await axios.post(`${API}/admin/products`, formData);
        toast.success("Букет добавлен");
      }
      onSave();
    } catch {
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="product-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold">{product ? "Редактировать" : "Новый букет"}</h2>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Image */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Фото</label>
            <div className="relative">
              {preview ? (
                <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100 mb-2">
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => { setImageFile(null); setPreview(product?.image || null); }}
                    className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-pink-400 cursor-pointer bg-gray-50 transition-colors">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Нажмите для загрузки</span>
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" data-testid="image-upload-input" />
                </label>
              )}
              {preview && (
                <label className="inline-flex items-center gap-2 text-sm text-pink-500 font-semibold cursor-pointer hover:underline">
                  <ImageIcon className="w-4 h-4" />
                  Заменить фото
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
              )}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Название</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Розовый микс"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none"
              data-testid="product-name-input"
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Цена ({CITIES.find(c => c.slug === city)?.currency || "$"})</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="150"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none"
              data-testid="product-price-input"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Категория</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none"
              data-testid="product-category-select"
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* City */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Город</label>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none"
              data-testid="product-city-select"
            >
              {CITIES.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white h-12 rounded-xl font-bold flex items-center justify-center gap-2"
            data-testid="product-save-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? "Сохранение..." : product ? "Сохранить" : "Добавить букет"}
          </Button>
        </form>
      </div>
    </div>
  );
}
